import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { filter, skip } from 'rxjs/operators';
import { UsuarioServiceProvider } from '../usuario-service/usuario-service';
import { OfflineDatabaseService } from './offline-database.service';
import { OfflineModeService } from './offline-mode.service';
import { MULTIPART_METHOD, OfflineTaskUploadSender, TaskUploadRequest } from './offline-task-upload.sender';
import { rowsOf } from './repositories/offline-repository';

export interface QueuedRequest {
  /** Un id por hito: pedir lo mismo dos veces reemplaza la petición anterior en vez de duplicarla. */
  id: string;
  /** URL completa; la cola no la completa porque la marca puede cambiar entre encolar y enviar. */
  url: string;
  /** `MULTIPART` es una entrega de tarea con archivos: el cuerpo es un `TaskUploadRequest`. */
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | typeof MULTIPART_METHOD;
  body?: unknown;
  /** 1 para las firmas (certificado, SST): salen después del resto. */
  priority?: 0 | 1;
  /** El curso al que pertenece, para saber qué mostrar o limpiar cuando ese curso se borre. */
  courseId?: number | null;
}

/** Resultado de reintentar a mano lo rechazado de un curso. */
export type RetryResult = 'offline' | 'sent';

/**
 * ¿El servidor rechazó la petición para siempre? Un 4xx (por ejemplo una firma ya registrada) no cambia
 * por reintentarlo. 401 (la sesión se cierra por otro lado), 408 y 429 son pasajeros: se reintentan.
 */
export function isPermanentRejection(error: any): boolean {
  const status = error?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && ![401, 408, 429].includes(status);
}

/** Cuánto se espera antes de vaciar la cola cuando vuelve la conexión (a que la red se asiente). */
const RECONNECT_FLUSH_DELAY_MS = 4000;

/**
 * La cola de peticiones que se hicieron sin conexión (`http_requests`): quien necesita enviar algo
 * al volver la red lo encola acá, y la cola lo envía en orden.
 *
 * Una petición que falla queda en la cola con su número de intentos y el último error, y se vuelve a
 * intentar en el siguiente vaciado. Solo se borra la que el servidor aceptó. Si el servidor la rechaza
 * para siempre (4xx) queda marcada `rejected`: no se reintenta sola, solo con `retry(courseId)`.
 */
@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  /** Los cursos que tienen envíos rechazados, para marcarlos en su tarjeta. */
  readonly rejectedCourseIds$ = new BehaviorSubject<number[]>([]);

  /**
   * Emite cuando terminó el vaciado de una vuelta a en línea: lo que se pudo enviar ya está en el servidor,
   * así que es el momento de traer de vuelta su estado. Solo el vaciado automático lo emite.
   */
  readonly reconnected$ = new Subject<void>();

  private flushing: Promise<void> | null = null;
  private networkSubscription: Subscription | null = null;
  private logoutSubscription: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private offlineDb: OfflineDatabaseService,
    private mode: OfflineModeService,
    private user: UsuarioServiceProvider,
    private taskUploads: OfflineTaskUploadSender
  ) {}

  /**
   * Guarda la petición. Si el disco falla, lanza: el llamador decide si avanzar con el hito o avisar.
   * Las del mismo `id` se reemplazan, y la nueva queda al final del orden.
   */
  async enqueue(request: QueuedRequest): Promise<void> {
    const db = await this.offlineDb.get();
    if (!db) throw new Error('La base offline no está disponible para encolar ' + request.id);
    await db.executeSql(
      `INSERT OR REPLACE INTO http_requests (id, url, method, data, is_sst, seq, attempts, last_error, course_id, rejected)
       VALUES (?, ?, ?, ?, ?, (SELECT COALESCE(MAX(seq), 0) + 1 FROM http_requests), 0, NULL, ?, 0)`,
      [
        request.id,
        request.url,
        request.method,
        request.body === undefined ? null : JSON.stringify(request.body),
        request.priority ?? 0,
        request.courseId ?? null,
      ]
    );
  }

  /**
   * Envía lo pendiente: primero lo normal y al final las firmas, cada grupo en el orden en que se
   * encoló. Si ya hay un vaciado en curso, devuelve ese mismo, así que se puede esperar.
   * Nunca rechaza: lo que falle se queda en la cola y se registra.
   */
  flush(delayMs = 0): Promise<void> {
    if (this.flushing) return this.flushing;
    this.flushing = this.runFlush(delayMs).finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  /**
   * Vuelve a dejar pendientes los envíos rechazados de un curso y los manda. Con la app en «Modo sin
   * conexión» no envía nada. Si el servidor los rechaza otra vez, quedan marcados de nuevo.
   */
  async retry(courseId: number): Promise<RetryResult> {
    if (this.mode.isOffline()) return 'offline';
    const db = await this.offlineDb.get();
    if (db && (await db.tableExists('http_requests'))) {
      await db.executeSql(
        'UPDATE http_requests SET rejected = 0, attempts = 0, last_error = NULL WHERE course_id = ? AND rejected = 1',
        [courseId]
      );
    }
    if (this.flushing) await this.flushing;
    await this.flush();
    return 'sent';
  }

  /** ¿Tiene el curso envíos esperando salir? (Los rechazados no cuentan: solo salen con `retry`.) */
  async hasPending(courseId: number): Promise<boolean> {
    const db = await this.offlineDb.get();
    if (!db || !(await db.tableExists('http_requests'))) return false;
    const res = await db.executeSql(
      'SELECT COUNT(*) AS total FROM http_requests WHERE course_id = ? AND COALESCE(rejected, 0) = 0',
      [courseId]
    );
    return res.rows.length > 0 && Number(res.rows.item(0).total) > 0;
  }

  /** Cuántos envíos rechazados tiene el curso (para avisar antes de borrarlo). */
  async countRejected(courseId: number): Promise<number> {
    const db = await this.offlineDb.get();
    if (!db || !(await db.tableExists('http_requests'))) return 0;
    const res = await db.executeSql(
      'SELECT COUNT(*) AS total FROM http_requests WHERE course_id = ? AND rejected = 1',
      [courseId]
    );
    return res.rows.length > 0 ? Number(res.rows.item(0).total) || 0 : 0;
  }

  /**
   * Borra de la cola lo rechazado del curso: se llama al quitar el curso de las descargas. Si entre eso
   * hay una entrega de tarea, sus archivos se borran también: ya no van a salir.
   */
  async discardRejected(courseId: number): Promise<void> {
    const db = await this.offlineDb.get();
    if (!db || !(await db.tableExists('http_requests'))) return;
    const uploads = rowsOf(
      await db.executeSql(
        'SELECT data FROM http_requests WHERE course_id = ? AND rejected = 1 AND method = ?',
        [courseId, MULTIPART_METHOD]
      )
    );
    for (const row of uploads) {
      try {
        await this.taskUploads.discard(JSON.parse(row.data) as TaskUploadRequest);
      } catch (error) {
        console.error('[OfflineQueueService] no se pudieron borrar los archivos de una entrega descartada', error);
      }
    }
    await db.executeSql('DELETE FROM http_requests WHERE course_id = ? AND rejected = 1', [courseId]);
    await this.refreshRejected();
  }

  /** Lee qué cursos tienen envíos rechazados y lo publica en `rejectedCourseIds$`. */
  async refreshRejected(): Promise<void> {
    try {
      const db = await this.offlineDb.get();
      if (!db || !(await db.tableExists('http_requests'))) {
        this.rejectedCourseIds$.next([]);
        return;
      }
      const rows = rowsOf(
        await db.executeSql(
          'SELECT DISTINCT course_id FROM http_requests WHERE rejected = 1 AND course_id IS NOT NULL',
          []
        )
      );
      this.rejectedCourseIds$.next(rows.map((row) => row.course_id));
    } catch (error) {
      console.error('[OfflineQueueService] no se pudo leer lo rechazado', error);
    }
  }

  /**
   * Vacía la cola cada vez que el modo de la app pasa a en línea, venga de donde venga: el teléfono
   * recupera la red, el alumno desactiva «Modo sin conexión» o la app arranca en línea. Es idempotente:
   * una sola suscripción, por más veces que se inicie la sesión, y se cancela al cerrar sesión.
   */
  startAutoFlush(): void {
    this.stopAutoFlush();
    this.networkSubscription = this.mode.isOffline$.subscribe((isOffline) => {
      if (!isOffline) {
        void this.flush(RECONNECT_FLUSH_DELAY_MS).then(() => this.reconnected$.next());
      }
    });
    // `isLogged` repite su valor actual al suscribirse; solo interesa el cierre de sesión posterior.
    this.logoutSubscription = this.user.isLogged
      .pipe(
        skip(1),
        filter((isLogged) => !isLogged)
      )
      .subscribe(() => this.stopAutoFlush());
  }

  stopAutoFlush(): void {
    this.networkSubscription?.unsubscribe();
    this.networkSubscription = null;
    this.logoutSubscription?.unsubscribe();
    this.logoutSubscription = null;
  }

  private async runFlush(delayMs: number): Promise<void> {
    if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      const db = await this.offlineDb.get();
      if (!db) return;
      if (!(await db.tableExists('http_requests'))) return;

      const pending = rowsOf(
        await db.executeSql(
          'SELECT * FROM http_requests WHERE COALESCE(rejected, 0) = 0 ORDER BY is_sst ASC, COALESCE(seq, rowid) ASC',
          []
        )
      );
      console.log('[OfflineQueueService] peticiones pendientes:', pending.length);

      for (const request of pending) {
        try {
          await this.send(request);
          await db
            .executeSql('DELETE FROM http_requests WHERE id = ?', [request.id])
            .then(() => console.log('[OfflineQueueService] enviada y eliminada:', request.id))
            .catch((error) =>
              console.error('[OfflineQueueService] error al eliminar la solicitud pendiente:', request.id, error)
            );
        } catch (error) {
          console.error(`[OfflineQueueService] error al procesar ${request.id}, sigue con la siguiente.`, error);
          await this.markFailed(db, request.id, error);
        }
      }
    } catch (error) {
      console.error('[OfflineQueueService] no se pudo vaciar la cola', error);
    }
    await this.refreshRejected();
  }

  private async send(request: { method: string; url: string; data: string }): Promise<void> {
    if (request.method === MULTIPART_METHOD) {
      await this.taskUploads.send(JSON.parse(request.data) as TaskUploadRequest);
    } else if (request.method === 'GET') {
      await this.http.request(request.method, request.url).toPromise();
    } else {
      await this.http.request(request.method, request.url, { body: request.data }).toPromise();
    }
  }

  private async markFailed(
    db: { executeSql(sql: string, params?: any[]): Promise<any> },
    id: string,
    error: any
  ): Promise<void> {
    const reason = String(error?.status ? `HTTP ${error.status}` : error?.message ?? error).slice(0, 500);
    try {
      await db.executeSql(
        'UPDATE http_requests SET attempts = COALESCE(attempts, 0) + 1, last_error = ?, rejected = ? WHERE id = ?',
        [reason, isPermanentRejection(error) ? 1 : 0, id]
      );
    } catch (updateError) {
      console.error('[OfflineQueueService] no se pudo registrar el error de', id, updateError);
    }
  }
}
